// API Client for the new backend

// Dev mode: allow localStorage override for testing multiple server instances
function getApiUrl(): string {
  if (import.meta.env.DEV) {
    const override = localStorage.getItem('dev_api_url');
    if (override) return override;
  }
  return import.meta.env.VITE_API_URL || 'http://localhost:3000';
}

let API_URL = getApiUrl();

// Allow runtime updates (for dev server switcher)
export function setApiUrl(url: string): void {
  localStorage.setItem('dev_api_url', url);
  API_URL = url;
}

export function getCurrentApiUrl(): string {
  return API_URL;
}

export interface User {
  id: string;
  discordId: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  role?: 'superuser';
  canCreateGames?: boolean;
}

interface AuthState {
  token: string | null;
  user: User | null;
}

// In-memory auth state
let authState: AuthState = {
  token: localStorage.getItem('token'),
  user: null,
};

// Decode JWT payload without verification (verification happens server-side)
function decodeJwtPayload(token: string): User | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));

    // Check if token is expired
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      return null;
    }

    // Map JWT claims to User object
    return {
      id: payload.sub || payload.id,
      discordId: payload.discordId,
      username: payload.username,
      displayName: payload.displayName,
      avatarUrl: payload.avatarUrl,
      role: payload.role,
      canCreateGames: payload.canCreateGames,
    };
  } catch {
    return null;
  }
}

// Event listeners for auth changes
type AuthListener = (user: User | null) => void;
const authListeners: Set<AuthListener> = new Set();

export function onAuthChange(listener: AuthListener): () => void {
  authListeners.add(listener);
  // Immediately call with current state
  listener(authState.user);
  return () => authListeners.delete(listener);
}

function notifyAuthListeners() {
  authListeners.forEach(listener => listener(authState.user));
}

// Initialize auth state from stored token
// Decodes JWT immediately for user info, then refreshes in background
export function initAuth(): User | null {
  const token = localStorage.getItem('token');
  if (!token) {
    authState = { token: null, user: null };
    notifyAuthListeners();
    return null;
  }

  // Decode JWT immediately - no server call needed
  const user = decodeJwtPayload(token);
  if (!user) {
    // Token is expired or malformed, clear it
    localStorage.removeItem('token');
    authState = { token: null, user: null };
    notifyAuthListeners();
    return null;
  }

  // Set user immediately from JWT
  authState = { token, user };
  notifyAuthListeners();

  // Refresh token in background (non-blocking)
  refreshTokenInBackground(token);

  return user;
}

// Background token refresh - updates token if server provides a new one
async function refreshTokenInBackground(token: string): Promise<void> {
  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // Token rejected by server - clear auth
      localStorage.removeItem('token');
      authState = { token: null, user: null };
      notifyAuthListeners();
      return;
    }

    const data = await response.json();

    // If server returned a new token (auto-refresh), update it
    if (data.token && data.token !== token) {
      localStorage.setItem('token', data.token);
      authState = { token: data.token, user: data.user };
      notifyAuthListeners();
    }
  } catch (error) {
    // Network error - keep using decoded JWT, don't clear auth
    console.error('Background token refresh failed (network error):', error);
  }
}

export function getToken(): string | null {
  return authState.token;
}

export function getUser(): User | null {
  return authState.user;
}

export function isAuthenticated(): boolean {
  return authState.token !== null && authState.user !== null;
}

export function isSuperuser(): boolean {
  return authState.user?.role === 'superuser';
}

export function canCreateGames(): boolean {
  return authState.user?.role === 'superuser' || authState.user?.canCreateGames === true;
}

// Login with magic code
export async function loginWithMagicCode(code: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_URL}/auth/magic`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const error = await response.json();
      return {
        success: false,
        error: error.error || 'Failed to validate code',
      };
    }

    const data = await response.json();
    localStorage.setItem('token', data.token);
    authState = { token: data.token, user: data.user };
    notifyAuthListeners();

    return { success: true };
  } catch (error) {
    console.error('Error logging in with magic code:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// Dev login (only works in development)
export async function devLogin(
  discordId: string,
  username: string,
  options?: { role?: 'superuser'; canCreateGames?: boolean }
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${API_URL}/auth/dev`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        discordId,
        username,
        role: options?.role,
        canCreateGames: options?.canCreateGames,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      return {
        success: false,
        error: error.error || 'Dev login failed',
      };
    }

    const data = await response.json();
    localStorage.setItem('token', data.token);
    authState = { token: data.token, user: data.user };
    notifyAuthListeners();

    return { success: true };
  } catch (error) {
    console.error('Error with dev login:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// Logout
export function logout(): void {
  localStorage.removeItem('token');
  authState = { token: null, user: null };
  notifyAuthListeners();
}

// Refresh token
export async function refreshToken(): Promise<boolean> {
  if (!authState.token) return false;

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authState.token}`,
      },
    });

    if (!response.ok) {
      // Only logout on auth failures (401), not other errors
      if (response.status === 401) {
        logout();
      }
      return false;
    }

    const data = await response.json();
    localStorage.setItem('token', data.token);
    authState.token = data.token;
    return true;
  } catch (error) {
    // Network error - don't purge token, user might just be offline
    console.error('Failed to refresh token (network error):', error);
    return false;
  }
}

// Generic API request helper
export async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: HeadersInit = {
    ...options.headers,
  };

  // Only set Content-Type for requests with a body
  if (options.body) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  if (authState.token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${authState.token}`;
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

// Game types (matching server response)
export interface Game {
  id: string;
  name: string;
  ownerUserId: string;
  stableVersion: number;
  betaVersion?: number;
  boxArtUrl?: string;
  gameplayImageUrl?: string;
  minPlayers?: number;
  maxPlayers?: number;
  licenseInformation?: string;
  implementationNotes?: string;
  publisher?: string;
  designer?: string;
  artist?: string;
  tutorialVideoUrl?: string;
  rulesPdfUrl?: string;
  purchaseUrl?: string;
  publisherUrl?: string;
  repositoryUrl?: string;
  versions: {
    [v: number]: {
      stable: string;
      beta?: string;
    };
  };
  visibility?: 'public' | 'unlisted' | 'private' | 'archived';
  testUserIds?: string[];
}

export interface ConfigurationChoice {
  label: string;
  id: string;
}

export interface ConfigurationOption {
  fieldName: string;
  config: {
    label: string;
    choices: ConfigurationChoice[];
    /** When true the option holds any number of the choices' ids (a string[]) rather than exactly one */
    multiple?: boolean;
  };
  /** The game's default: a choice id, or an array of ids when `multiple` */
  currentValue?: string | string[];
}

/** A way to start the game without a lobby: the plain demo or a scripted tutorial */
export interface PublishedDemo {
  id: string;
  kind: 'demo' | 'tutorial';
  /** In the first locale listed */
  title: string;
  /** Tutorials: the locales it comes in, and its title in each */
  locales?: string[];
  titles?: Record<string, string>;
  steps?: number;
  /** Tutorials: ids of the tutorials it assumes the player has done */
  requires?: string[];
}

export interface GameVersion {
  id: string;
  version: string;
  name: string;
  description: string;
  major: number;
  maxPlayers: number;
  minPlayers: number;
  webEntry: string;
  nodeEntry: string;
  backgroundColor: string;
  foregroundColor: string;
  boxArt?: string;
  screenshot?: string;
  licenseInformation?: string;
  implementationNotes?: string;
  publisher?: string;
  designer?: string;
  artist?: string;
  tutorialVideoUrl?: string;
  rulesPdfUrl?: string;
  purchaseUrl?: string;
  publisherUrl?: string;
  repositoryUrl?: string;
  configurationOptions?: ConfigurationOption[];
  coreVersion?: string;
  demos?: PublishedDemo[];
  /** Whether this build's bundled engine can seat AI players */
  supportsAi?: boolean;
}

/**
 * Input type for creating a new game version.
 * Used by both initial game creation and adding new versions to existing games.
 * Versions are immutable once created.
 */
export interface GameVersionInput {
  version: string;        // e.g., "1.0", "2.5"
  name: string;
  description: string;
  major: number;
  maxPlayers: number;
  minPlayers: number;
  webEntry: string;
  nodeEntry: string;
  backgroundColor: string;
  foregroundColor: string;
  boxArt?: string;
  screenshot?: string;
  licenseInformation?: string;
  implementationNotes?: string;
  publisher?: string;
  designer?: string;
  artist?: string;
  tutorialVideoUrl?: string;
  rulesPdfUrl?: string;
  purchaseUrl?: string;
  publisherUrl?: string;
  repositoryUrl?: string;
  configurationOptions?: ConfigurationOption[];
  coreVersion?: string;
  demos?: PublishedDemo[];
}

/** Marks a seat as played by the engine's AI. */
export interface AiSeatInfo {
  /** 0–1; lower plays less predictably. */
  difficulty?: number;
}

export interface Player {
  odId: string;
  name: string;
  imageUrl?: string | null;
  prompt?: string;
  promptTime?: number;
  score?: number;
  ai?: AiSeatInfo;
}

export type VersionChannel = 'stable' | 'beta';
export type MatchStatus = 'pending' | 'active' | 'finished' | 'cancelled' | 'abandoned' | 'preparing';
export type MatchVisibility = 'public' | 'unlisted' | 'private';
/** 'ai': one human against the AI, private and always run in the browser */
export type PlayMode = 'multiplayer' | 'ai';
export type AiDifficultyLevel = 'low' | 'medium' | 'high';

export interface Match {
  id: string;
  name: string;
  gameName: string;
  createdByUserId: string;
  createdByName: string;
  status: MatchStatus;
  visibility: MatchVisibility;
  numPlayers: number;
  gameId: string;
  majorVersion: number;
  minorVersionChannel: VersionChannel;
  playerUserIds: string[];
  players: {
    [odId: string]: Player;
  };
  options?: Record<string, string | string[]>;
  playMode?: PlayMode;
  createdAt: string;
  lastUpdated?: string;
  discordThreadId?: string;
  discordGuildId?: string;
}

export interface ClusterServer {
  serverId: string;
  internalAddress: string;
  internalPort: number;
  status: 'active' | 'draining';
  heartbeat: number;
  heartbeatAgeMs: number;
  processCount: number;
  matchCount: number;
  connectionCount: number;
  loadedBundles: string[];
  isThisServer: boolean;
}

export interface ClusterStatus {
  thisServer: {
    serverId: string;
    internalAddress: string;
    internalPort: number;
    denoProcesses: {
      processCount: number;
      totalMatches: number;
      bundlesLoaded: number;
      totalMemoryMb: number;
      totalMemoryLimitMb: number;
      targetMemoryMb: number;
      perProcessMemoryLimitMb: number;
      maxProcesses: number;
      processDetails: Array<{
        processId: string;
        bundleCount: number;
        matchCount: number;
        connectionCount: number;
        memoryUsedMb: number;
      }>;
    };
    ownedMatchCount: number;
    ownedMatches: string[];
  };
  cluster: {
    serverCount: number;
    servers: ClusterServer[];
  };
  locks: {
    totalOwned: number;
    epoch: number;
    matches: Array<{
      matchId: string;
      ownerServerId: string;
      epoch: number;
    }>;
  };
}

// API methods
export const api = {
  // Games
  getGames: () => apiRequest<{ games: Game[] }>('/api/games'),
  getGame: (id: string) => apiRequest<{ game: Game }>(`/api/games/${id}`),
  getGameWithVersion: (
    id: string,
    options?: { version?: string; majorVersion?: number; channel?: VersionChannel }
  ) => {
    const params = new URLSearchParams();
    if (options?.version) params.set('version', options.version);
    if (options?.majorVersion !== undefined) params.set('majorVersion', String(options.majorVersion));
    if (options?.channel) params.set('channel', options.channel);
    const query = params.toString();
    return apiRequest<{ game: Game; version: GameVersion }>(
      `/api/games/${id}/with-version${query ? `?${query}` : ''}`
    );
  },

  // Game config (read-only - for editing UI)
  getGameConfig: (id: string) =>
    apiRequest<{ game: Game; config: unknown }>(`/api/games/${id}/config`),

  // Create a new game with its initial version (both created atomically)
  // The game's name and display fields are derived from the initial version
  createGame: (data: { id: string; initialVersion: GameVersionInput }) =>
    apiRequest<{ game: Game; version: GameVersion }>('/api/games', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update game channel settings
  // Note: name, minPlayers, maxPlayers, boxArtUrl, gameplayImageUrl are always
  // derived from the stable version's stable build - cannot be set directly
  // All fields are required - betaVersion uses null to clear
  updateGame: (id: string, data: {
    stableVersion: number;
    betaVersion: number | null;
    versions: Record<number, { stable: string; beta?: string }>;
    testUserIds: string[];
    visibility?: 'public' | 'unlisted' | 'private' | 'archived';
  }) =>
    apiRequest<{ game: Game }>(`/api/games/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Game versions (immutable - can only create, not update)
  getVersions: (gameId: string) =>
    apiRequest<{ versions: GameVersion[] }>(`/api/games/${gameId}/versions`),
  getVersion: (gameId: string, versionId: string) =>
    apiRequest<{ version: GameVersion }>(`/api/games/${gameId}/versions/${versionId}`),
  createVersion: (gameId: string, versionData: GameVersionInput) =>
    apiRequest<{ version: GameVersion }>(`/api/games/${gameId}/versions`, {
      method: 'POST',
      body: JSON.stringify(versionData),
    }),

  // Matches
  getMatches: (params?: { gameId?: string; status?: string; playerId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.gameId) searchParams.set('gameId', params.gameId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.playerId) searchParams.set('playerId', params.playerId);
    const query = searchParams.toString();
    return apiRequest<{ matches: Match[] }>(`/api/matches${query ? `?${query}` : ''}`);
  },
  getMatch: (id: string) => apiRequest<{ match: Match }>(`/api/matches/${id}`),
  getMatchDebug: (id: string) => apiRequest<{ match: Match & { state?: Record<string, unknown> } }>(`/api/matches/${id}/debug`),
  /** The match document exactly as stored, every field included (game owner or superuser only) */
  getMatchRaw: (id: string) => apiRequest<{ match: Record<string, unknown> }>(`/api/matches/${id}/debug?raw=1`),
  createMatch: (data: {
    gameId: string;
    numPlayers: number;
    visibility?: string;
    majorVersion?: number;
    options?: Record<string, string | string[]>;
    playMode?: PlayMode;
    aiDifficulty?: AiDifficultyLevel;
  }) =>
    apiRequest<{ match: Match }>('/api/matches', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  joinMatch: (id: string) =>
    apiRequest<{ match: Match }>(`/api/matches/${id}/join`, { method: 'POST' }),
  leaveMatch: (id: string) =>
    apiRequest<{ match: Match }>(`/api/matches/${id}/leave`, { method: 'POST' }),
  updateMatch: (id: string, data: { status?: MatchStatus; visibility?: MatchVisibility; minorVersionChannel?: VersionChannel }) =>
    apiRequest<{ match: Match }>(`/api/matches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  // AI seats (pending matches only)
  addAiSeat: (id: string, difficulty: 'low' | 'medium' | 'high') =>
    apiRequest<{ match: Match }>(`/api/matches/${id}/ai`, {
      method: 'POST',
      body: JSON.stringify({ difficulty }),
    }),
  setAiSeatDifficulty: (id: string, seatId: string, difficulty: 'low' | 'medium' | 'high') =>
    apiRequest<{ match: Match }>(`/api/matches/${id}/ai/${seatId}`, {
      method: 'PUT',
      body: JSON.stringify({ difficulty }),
    }),
  removeAiSeat: (id: string, seatId: string) =>
    apiRequest<{ match: Match }>(`/api/matches/${id}/ai/${seatId}`, { method: 'DELETE' }),
  updateMatchStateAdmin: (id: string, state: Record<string, unknown>) =>
    apiRequest<{ match: Match }>(`/api/matches/${id}/admin-state`, {
      method: 'PUT',
      body: JSON.stringify({ state }),
    }),

  // Users
  getUser: (id: string) => apiRequest<{ user: User }>(`/api/users/${id}`),
  searchUsers: (q: string) => apiRequest<{ users: User[] }>(`/api/users/search?q=${encodeURIComponent(q)}`),
  getUsersByIds: (ids: string[]) =>
    apiRequest<{ users: User[] }>('/api/users/batch', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  updateUserPermissions: (id: string, data: { role?: 'superuser' | null; canCreateGames?: boolean }) =>
    apiRequest<{ user: unknown }>(`/api/users/${id}/permissions`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Admin
  getClusterStatus: () => apiRequest<ClusterStatus>('/api/admin/cluster-status'),
};
